import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useHubMember } from "@/lib/HubMemberContext";
import { UserPlus, Loader2, Mail, Lock, User, Phone } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

/**
 * Self-serve signup — no admin needed. Creates a role=parent user + hub member,
 * signs the user in immediately, and lands them on /me.
 */
export default function MemberSignup() {
  const navigate = useNavigate();
  const { refresh } = useHubMember();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) {
      toast.error("Name, email and a 6+ character password are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/hub/signup`,
        { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), password: form.password },
        { withCredentials: true },
      );
      toast.success("Welcome to HTP Sports Hub!");
      await refresh();
      navigate("/me", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-[calc(100vh-80px)] py-16 px-5" data-testid="member-signup-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// New here</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Create Account.</h1>
          <p className="text-sm text-white/60 mt-3">
            Sign up once — book slots, buy memberships, enrol your kids in the academy, and track everything in one place.
          </p>
        </div>

        <form onSubmit={submit} className="border border-white/12 bg-[#131A2B] p-6 space-y-3">
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">Full name</div>
            <Field icon={User} value={form.name} onChange={set("name")} data-testid="signup-name" placeholder="Your name" autoFocus />
          </label>
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">Email</div>
            <Field icon={Mail} type="email" value={form.email} onChange={set("email")} data-testid="signup-email" placeholder="you@example.com" autoComplete="email" />
          </label>
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">WhatsApp phone (optional now, needed later)</div>
            <Field icon={Phone} type="tel" value={form.phone} onChange={set("phone")} data-testid="signup-phone" placeholder="+91 98765 43210" />
          </label>
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">Password</div>
            <Field icon={Lock} type="password" value={form.password} onChange={set("password")} data-testid="signup-password" placeholder="At least 6 characters" autoComplete="new-password" />
          </label>
          <button type="submit" disabled={busy}
            data-testid="signup-submit"
            className="w-full bg-brand-orange text-black py-3 font-bold text-sm uppercase tracking-widest hover:bg-white transition disabled:opacity-50 inline-flex items-center justify-center gap-2 mt-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>

        <div className="mt-6 relative">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/12" /></div>
          <div className="relative text-center">
            <span className="bg-[#0B1120] px-3 text-[10px] font-mono uppercase tracking-widest text-white/40">or use Google</span>
          </div>
        </div>
        <div className="mt-4 text-center">
          <GoogleSignInButton returnTo="/me" label="Continue with Google" />
        </div>

        <div className="mt-8 text-center text-xs text-white/50">
          Already have an account? <Link to="/member/login" className="brand-orange">Sign in</Link>
        </div>
      </div>
    </section>
  );
}

function Field({ icon: Icon, ...props }) {
  return (
    <div className="flex items-center border border-white/20 focus-within:border-brand-orange bg-[#0B1120]">
      <Icon size={14} className="ml-3 text-white/40" />
      <input {...props} className="w-full bg-transparent py-2.5 px-3 text-sm outline-none" />
    </div>
  );
}

